// The resin's orange-peel micro-texture as a tangent-space normal map: SLA
// layer lines (fine parallel ribbing with a little wobble and jitter) plus a
// touch of fine isotropic grain, the print's. Soft undulation, NOT streaky
// scratches. ~50 µm layers on the ~87 mm object: at the material's repeat of
// 13 the lines land near true scale; at page zoom they are subpixel and read
// as sheen, resolving only up close.
//
// Pure JS on typed arrays (no canvas, nothing read back from the GPU), so it
// costs main-thread arithmetic only, and seeded, so every load (and the
// poster's render, scripts/render-poster.mjs) gets the same texture.
//
// orangePeelNormals({ size, seed }) -> Uint8Array, size × size RGBA texels,
// row 0 first: the normal (x, y, z) mapped from -1..1 to 0..255; alpha 255.

export const ORANGE_PEEL_SIZE = 512;
export const ORANGE_PEEL_SEED = 0x5eed;

// the grain: white noise softened by a Gaussian of this standard deviation (px)
const GRAIN_BLUR_SIGMA = 1.5;
// the layer lines: this many across the texture; their height, and the
// grain's, in 0..255 height units
const LINES = 128;
const LINE_AMPLITUDE = 26;
const GRAIN_AMPLITUDE = 0.25;
// how steep the heights make the normals
const BUMP = 2.0;

// mulberry32: a small, fast seeded PRNG, uniform in [0, 1)
function random(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// The grain: 8-bit white noise around 128, blurred by a separable Gaussian
// whose weights are renormalized over the taps inside the texture at its
// edges (what a 2D canvas's blur() filter does to an opaque image drawn on a
// transparent canvas: the edge's missing taps are transparent, and the
// result unpremultiplied), then held at 8 bits.
function grain(size, rand) {
  const noise = new Uint8ClampedArray(size * size);
  for (let i = 0; i < noise.length; i++) noise[i] = 128 + (rand() - 0.5) * 255;
  const radius = Math.ceil(3 * GRAIN_BLUR_SIGMA);
  const kernel = new Float64Array(2 * radius + 1);
  for (let k = -radius; k <= radius; k++) kernel[k + radius] = Math.exp(-(k * k) / (2 * GRAIN_BLUR_SIGMA * GRAIN_BLUR_SIGMA));
  const pass = (src, dst, step, along) => {
    for (let line = 0; line < size; line++) {
      const base = line * along;
      for (let p = 0; p < size; p++) {
        let sum = 0, weight = 0;
        const lo = Math.max(-radius, -p), hi = Math.min(radius, size - 1 - p);
        for (let k = lo; k <= hi; k++) {
          const w = kernel[k + radius];
          sum += w * src[base + (p + k) * step];
          weight += w;
        }
        dst[base + p * step] = sum / weight;
      }
    }
  };
  const rows = new Float32Array(size * size);
  pass(noise, rows, 1, size); // along each row
  const out = new Uint8ClampedArray(size * size);
  pass(rows, out, size, 1); // along each column
  return out;
}

export function orangePeelNormals({ size = ORANGE_PEEL_SIZE, seed = ORANGE_PEEL_SEED } = {}) {
  const fine = grain(size, random(seed));
  const h = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    const wob = Math.sin(y * 0.11) * 0.8;
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const line = Math.sin((y + wob + fine[i] * 0.01) * (Math.PI * 2 * LINES / size));
      h[i] = 128 + line * LINE_AMPLITUDE + (fine[i] - 128) * GRAIN_AMPLITUDE;
    }
  }
  // normals from the heights' central differences, wrapping (the texture repeats)
  const out = new Uint8ClampedArray(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const hx = h[y * size + ((x + 1) % size)] - h[y * size + ((x - 1 + size) % size)];
      const hy = h[((y + 1) % size) * size + x] - h[((y - 1 + size) % size) * size + x];
      const nx = (-hx * BUMP) / 255, ny = (-hy * BUMP) / 255;
      const l = Math.hypot(nx, ny, 1);
      out[i * 4] = (nx / l * 0.5 + 0.5) * 255;
      out[i * 4 + 1] = (ny / l * 0.5 + 0.5) * 255;
      out[i * 4 + 2] = (1 / l * 0.5 + 0.5) * 255;
      out[i * 4 + 3] = 255;
    }
  }
  return new Uint8Array(out.buffer);
}
